import React, {Component} from "react";
import i18nService from "../../i18n/i18nService";
import './languageSelector.css';

interface LanguageSelectorProps {
    ephemeral?: boolean; // If true, changes are not persisted (for login page)
    onLocaleChange?: (locale: string) => void;
}

interface LanguageSelectorState {
    isOpen: boolean;
    currentLocale: string;
    dropdownPosition: { top: number; right: number } | null;
}

class LanguageSelector extends Component<LanguageSelectorProps, LanguageSelectorState> {
    private unsubscribe?: () => void;

    private buttonRef: React.RefObject<HTMLButtonElement>;

    constructor(props: LanguageSelectorProps) {
        super(props);
        this.buttonRef = React.createRef();
        this.state = {
            isOpen: false,
            currentLocale: i18nService.getLocale(),
            dropdownPosition: null,
        };
        this.handleToggle = this.handleToggle.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        this.unsubscribe = i18nService.subscribe(() => {
            this.setState({ currentLocale: i18nService.getLocale() });
        });
        document.addEventListener('click', this.handleClickOutside);
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        document.removeEventListener('click', this.handleClickOutside);
    }

    handleToggle(e: React.MouseEvent) {
        e.stopPropagation();
        const newIsOpen = !this.state.isOpen;
        if (newIsOpen && this.buttonRef.current) {
            const rect = this.buttonRef.current.getBoundingClientRect();
            this.setState({ 
                isOpen: newIsOpen,
                dropdownPosition: {
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                }
            });
        } else {
            this.setState({ isOpen: newIsOpen, dropdownPosition: null });
        }
    }

    async handleSelect(locale: string) {
        i18nService.setLocale(locale as any, !this.props.ephemeral);
        this.setState({ isOpen: false, currentLocale: locale, dropdownPosition: null });
        
        // Update user's locale preference if not ephemeral
        if (!this.props.ephemeral) {
            const UserDetailsService = (await import("../services/userDetailsService")).default;
            await UserDetailsService.updateLocale(
                locale,
                async () => {
                    // Locale updated successfully
                },
                async (error: string) => {
                    console.error('Failed to update user locale:', error);
                },
                async () => {}
            );
        }
        
        if (this.props.onLocaleChange) {
            this.props.onLocaleChange(locale);
        }
    }

    handleClickOutside(e: MouseEvent) {
        const target = e.target as HTMLElement;
        if (!target.closest('.LanguageSelector-Container')) {
            this.setState({ isOpen: false, dropdownPosition: null });
        }
    }

    render() {
        const locales = i18nService.getSupportedLocales();
        const current = locales.find(l => l.code === this.state.currentLocale) || locales[0];

        return (
            <>
                <div className="LanguageSelector-Container">
                    <button
                        ref={this.buttonRef}
                        className="LanguageSelector-Button"
                        onClick={this.handleToggle}
                        aria-label="Select language"
                    >
                        <span className="LanguageSelector-Flag">{current.flag}</span>
                        <span className="LanguageSelector-Name">{current.name}</span>
                        <span className="LanguageSelector-Arrow">{this.state.isOpen ? '▲' : '▼'}</span>
                    </button>
                </div>
                {this.state.isOpen && this.state.dropdownPosition && (
                    <div 
                        className="LanguageSelector-Dropdown"
                        style={{
                            position: 'fixed',
                            top: `${this.state.dropdownPosition.top}px`,
                            right: `${this.state.dropdownPosition.right}px`,
                        }}
                    >
                        {locales.map(locale => (
                            <button
                                key={locale.code}
                                className={`LanguageSelector-Option ${this.state.currentLocale === locale.code ? 'LanguageSelector-Option-Active' : ''}`}
                                onClick={() => this.handleSelect(locale.code)}
                            >
                                <span className="LanguageSelector-Flag">{locale.flag}</span>
                                <span className="LanguageSelector-Name">{locale.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </>
        );
    }
}

export default LanguageSelector;

